import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { isLoanFutureType } from "web_myplann/Goals/Helpers/futureContractForm";
import { mapFuturePortfolioToFormValues } from "web_myplann/Goals/Mappers/mapFutureContractForm";
import {
	FuturePortfolioTypeEnum,
	type GoalFuturePortfolioResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { z } from "zod";

const formSchema = z.object({
	name: z.string().min(1),
	type: z.nativeEnum(FuturePortfolioTypeEnum),
	monthly: z.number().min(0),
	lumpSum: z.number().min(0),
	loanAmount: z.number().min(0),
	startDate: z.date(),
	note: z.string().optional(),
});

export type PinFutureContractFormValues = z.infer<typeof formSchema>;

export const usePinFutureContractForm = (
	open: boolean,
	editing?: GoalFuturePortfolioResponseDTO | null,
) => {
	const { t } = useResource();

	const form = useForm<PinFutureContractFormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: mapFuturePortfolioToFormValues(editing),
	});

	const { watch, formState, reset } = form;

	useEffect(() => {
		if (open) {
			reset(mapFuturePortfolioToFormValues(editing));
		}
	}, [open, editing, reset]);

	const type = watch("type");
	const monthly = watch("monthly");
	const lumpSum = watch("lumpSum");
	const loanAmount = watch("loanAmount");
	const name = watch("name");

	const isLoanType = isLoanFutureType(type);

	const canSubmit =
		name.trim().length > 0 &&
		(isLoanType ? loanAmount > 0 : monthly > 0 || lumpSum > 0) &&
		!formState.isSubmitting;

	const typeOptions = useMemo(
		() =>
			Object.values(FuturePortfolioTypeEnum).map((value) => ({
				value,
				label: t(Resources.Goals.Detail.Solution.Pin.Future.Types[value]),
			})),
		[t],
	);

	return {
		form,
		typeOptions,
		isLoanType,
		canSubmit,
	};
};
